import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';
import type {
  CampaignStatus,
  CampaignsInsert,
  CampaignsRow,
  CampaignsUpdate,
} from '@/types/database.types';

import {
  countCampaignLeads,
  countCampaignsByStatus,
  createCampaign,
  getCampaign,
  hardDeleteCampaign,
  listCampaigns,
  softDeleteCampaign,
  updateCampaign,
  type CampaignsFilters,
  type CampaignsListResult,
} from './api';

export const campaignsKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignsKeys.all, 'list'] as const,
  list: (filters: CampaignsFilters, page: number) =>
    [...campaignsKeys.lists(), filters, page] as const,
  counts: () => [...campaignsKeys.all, 'counts'] as const,
  details: () => [...campaignsKeys.all, 'detail'] as const,
  detail: (id: string) => [...campaignsKeys.details(), id] as const,
};

export function useCampaigns(filters: CampaignsFilters, page: number) {
  return useQuery<CampaignsListResult>({
    queryKey: campaignsKeys.list(filters, page),
    queryFn: () => listCampaigns(filters, page),
    staleTime: 15_000,
  });
}

export function useCampaignsStatusCounts() {
  return useQuery<Partial<Record<CampaignStatus, number>>>({
    queryKey: campaignsKeys.counts(),
    queryFn: countCampaignsByStatus,
    staleTime: 30_000,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery<CampaignsRow | null>({
    queryKey: id ? campaignsKeys.detail(id) : ['campaigns', 'detail', 'unset'],
    queryFn: () => getCampaign(id as string),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/** Leads conseguidos (lead_conversations nacidas de esta campaña). */
export function useCampaignLeadsCount(id: string | undefined) {
  return useQuery<number>({
    queryKey: id
      ? ([...campaignsKeys.detail(id), 'leads'] as const)
      : ['campaigns', 'detail', 'unset', 'leads'],
    queryFn: () => countCampaignLeads(id as string),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  const { user } = useSupabase();
  return useMutation<
    CampaignsRow,
    Error,
    Omit<CampaignsInsert, 'owner_user_id'>
  >({
    mutationFn: (input) => {
      if (!user) throw new Error('Sin sesión');
      return createCampaign({ ...input, owner_user_id: user.id });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsKeys.lists() });
      void qc.invalidateQueries({ queryKey: campaignsKeys.counts() });
      toast.success('Campaña creada');
    },
    onError: (e) =>
      toast.error('No se pudo crear', { description: e.message }),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation<
    CampaignsRow,
    Error,
    { id: string; patch: CampaignsUpdate }
  >({
    mutationFn: ({ id, patch }) => updateCampaign(id, patch),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: campaignsKeys.lists() });
      void qc.invalidateQueries({ queryKey: campaignsKeys.counts() });
      void qc.invalidateQueries({ queryKey: campaignsKeys.detail(row.id) });
    },
    onError: (e) =>
      toast.error('No se pudo actualizar', { description: e.message }),
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => softDeleteCampaign(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsKeys.lists() });
      void qc.invalidateQueries({ queryKey: campaignsKeys.counts() });
      toast.success('Campaña archivada');
    },
    onError: (e) =>
      toast.error('No se pudo archivar', { description: e.message }),
  });
}

/**
 * Hard-delete: borra la campaña para siempre. Solo se invoca desde el listado
 * de archivadas y solo cuando 0 emails fueron enviados. La validación final
 * vive en la API (re-cuenta campaign_emails antes de DELETE).
 */
export function useHardDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => hardDeleteCampaign(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsKeys.lists() });
      void qc.invalidateQueries({ queryKey: campaignsKeys.counts() });
      toast.success('Campaña eliminada para siempre');
    },
    onError: (e) =>
      toast.error('No se pudo eliminar', { description: e.message }),
  });
}
